import React, { useMemo } from 'react';

import {
	getProductAndIDFromARI,
	type SearchTrelloPartial,
} from '@atlassian/dynamic-cards-search-client';
import { highlightMatchedText } from '@atlassian/dynamic-cards-search-common';

import { ProductKeys } from '../../../../../../common/constants/products';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { ProductIcon } from '../../../../../../common/ui/product-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { truncateTextWithQuery } from '../../../../../../common/utils/truncate-text-with-query';
import {
	useBootstrap,
	usePrimaryProduct,
	useSelectedProduct,
} from '../../../../../../controllers/store';
import { useSearchQuery } from '../../../../../../controllers/store/search';

interface TrelloSearchResultProps {
	partial: SearchTrelloPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

export const TrelloSearchResult = ({
	partial,
	position,
}: TrelloSearchResultProps): React.JSX.Element => {
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();
	const [searchQuery] = useSearchQuery();

	const { id, title, url, type, description, lastModifiedDate } = partial;
	const metadata = useMemo(() => {
		const [product] = getProductAndIDFromARI(id);
		return {
			entityType: type === 'issue' ? 'card' : type,
			product,
		};
	}, [id, type]);

	const getLastModified = useMemo(() => {
		if (!lastModifiedDate) {
			return null;
		}
		return <LastModified lastModified={lastModifiedDate} />;
	}, [lastModifiedDate]);

	const enrichedTitle = useMemo(() => {
		const { highlightedTitle } = highlightMatchedText(title, searchQuery.split(' '));
		if (!highlightedTitle) {
			return title;
		}
		return enrichExcerpt(highlightedTitle);
	}, [title, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = truncateTextWithQuery(description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [description, searchQuery]);

	return (
		<SearchResult
			{...partial}
			primaryProduct={primaryProduct}
			url={url}
			icon={<ProductIcon product={ProductKeys.Trello} />}
			title={enrichedTitle}
			headerMetadata={
				<SearchMetadata
					product={metadata.product}
					productSelected={!!selectedProduct}
					parts={[
						['type', <SearchResultType type={metadata.entityType} />],
						['lastModified', getLastModified],
					]}
				/>
			}
			description={enrichedDescription}
			cloudId={cloudId}
			position={position}
		/>
	);
};
